// client/src/utils/permissions.js - VERSÃO OTIMIZADA
/**
 * Utilitários de permissões para o sistema baseado em scopes
 * @version 4.2 - Otimizado para reduzir logs desnecessários
 */

const DEBUG_MODE = process.env.NODE_ENV === 'development';
const LOG_PREFIX = '[PERMISSIONS]';

// ✅ SCOPES DO SERVIDOR
const SERVER_SCOPES = {
    MATRIZ: 'matriz',
    FRANQUIA: 'franquia',
    CONTA: 'conta',
    TERCEIRO: 'terceiro',
    CLIENTE: 'cliente'
};

// ✅ PERMISSÕES ESPECIAIS
const FULL_ACCESS_PERMISSIONS = {
    MATRIZ: 'matriz_admin:full_access',
    FRANQUIA: 'franquia_admin:full_access',
    CONTA: 'conta_admin:full_access',
    SUPER: 'super_admin:bypass_all_checks'
};

// ✅ Função de log controlada
const log = (level, message, data = null) => {
    if (!DEBUG_MODE) return;
    
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const prefix = `${LOG_PREFIX} [${timestamp}]`;
    
    switch(level) {
        case 'info':
            console.log(`${prefix} ℹ️ ${message}`, data || '');
            break;
        case 'success':
            console.log(`${prefix} ✅ ${message}`, data || '');
            break;
        case 'warn':
            console.warn(`${prefix} ⚠️ ${message}`, data || '');
            break;
        case 'error':
            console.error(`${prefix} ❌ ${message}`, data || '');
            break;
        case 'debug':
            console.debug(`${prefix} 🔍 ${message}`, data || '');
            break;
    }
};

/**
 * ✅ Obtém um accountId válido para o usuário (SILENCIOSO)
 */
export const getValidAccountId = (user) => {
    if (!user) {
        log('warn', 'getValidAccountId: usuário não definido');
        return null;
    }
    
    // 1. Prioridade: accountId direto do usuário
    if (user.accountId) {
        log('debug', `Usando accountId do usuário: ${user.accountId}`);
        return user.accountId;
    }
    
    // 2. Account do usuário
    if (user.account) {
        const accountId = user.account._id || user.account.id || user.account;
        log('debug', `Usando account do usuário: ${accountId}`);
        return accountId;
    }
    
    // 3. Para Matriz Admin: verificar localStorage
    const isMatriz = isMatrizAdmin(user);
    if (isMatriz) {
        const selectedAccountId = localStorage.getItem('matriz_selected_account') ||
                                 localStorage.getItem('selected_account_id');
        
        if (selectedAccountId) {
            log('debug', `Matriz usando conta selecionada: ${selectedAccountId}`);
            return selectedAccountId;
        }
        
        // Usar primeira conta disponível
        if (user.accounts && user.accounts.length > 0) {
            const firstAccount = user.accounts[0]?.account || user.accounts[0];
            const firstAccountId = firstAccount?._id || firstAccount?.id;
            if (firstAccountId) {
                log('debug', `Matriz usando primeira conta disponível: ${firstAccountId}`);
                return firstAccountId;
            }
        }
        
        log('warn', 'Matriz Admin sem conta disponível');
        return null;
    }
    
    // 4. Para outros usuários: usar conta vinculada
    const scope = getUserScope(user);
    if (scope === 'conta' && user.unidadePrincipal?.account) {
        const accountId = user.unidadePrincipal.account._id || user.unidadePrincipal.account;
        log('debug', `Usando conta da unidade principal: ${accountId}`);
        return accountId;
    }
    
    log('warn', 'Nenhum accountId encontrado');
    return null;
};

/**
 * ✅ Verifica se o usuário tem uma permissão específica (OTIMIZADO)
 */
export const hasPermission = (user, requiredPermission) => {
    if (!user || !user.role) {
        log('warn', `hasPermission: usuário ou role não definido`);
        return false;
    }
    
    const permissions = user.permissions || user.role.permissions || [];
    const userScope = getUserScope(user);
    
    // 1. SUPER ADMIN: Acesso a TUDO sem exceção
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.SUPER)) {
        log('debug', `SUPER ADMIN: bypass total para "${requiredPermission}"`);
        return true;
    }
    
    // 2. MATRIZ ADMIN: Acesso a TUDO sem exceção
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.MATRIZ)) {
        log('debug', `MATRIZ ADMIN: acesso total para "${requiredPermission}"`);
        return true;
    }
    
    // 3. FRANQUIA ADMIN: Acesso FULL ao seu escopo
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.FRANQUIA)) {
        const isExclusivelyMatriz = requiredPermission.startsWith('matriz_') || 
                                     requiredPermission.startsWith('plans_franquias');
        
        if (!isExclusivelyMatriz) {
            log('debug', `FRANQUIA ADMIN: acesso permitido para "${requiredPermission}"`);
            return true;
        }
    }
    
    // 4. CONTA ADMIN: Acesso FULL operacional
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.CONTA)) {
        const isRestricted = requiredPermission.startsWith('matriz_') || 
                             requiredPermission.startsWith('franquias:') || 
                             requiredPermission.startsWith('plans_');
        
        if (!isRestricted) {
            log('debug', `CONTA ADMIN: acesso permitido para "${requiredPermission}"`);
            return true;
        }
    }
    
    // 5. Permissões específicas
    if (permissions.includes(requiredPermission)) {
        log('debug', `Permissão específica encontrada: "${requiredPermission}"`);
        return true;
    }
    
    // 6. Permissões de wildcard
    const [resource, action] = requiredPermission.split(':');
    const wildcardPermission = `${resource}:*`;
    if (permissions.includes(wildcardPermission)) {
        log('debug', `Wildcard permission: "${wildcardPermission}" cobre "${requiredPermission}"`);
        return true;
    }
    
    log('debug', `Permissão NEGADA: "${requiredPermission}" | Scope: ${userScope}`);
    return false;
};

/**
 * ✅ CORREÇÃO: Verifica acesso a rota específica (SILENCIOSO)
 */
export const canAccessRoute = (user, routePath) => {
    if (!user || !user.role) {
        log('warn', `canAccessRoute: usuário não autenticado`);
        return false;
    }
    
    const scope = getUserScope(user);
    const permissions = user.permissions || user.role.permissions || [];
    
    // Mapeamento de rotas para scopes e permissões
    const routePermissions = {
        '/franquias': {
            requiredScope: SERVER_SCOPES.MATRIZ,
            requiredPermission: 'franquias:view_all',
            fullAccessPermissions: [FULL_ACCESS_PERMISSIONS.MATRIZ, FULL_ACCESS_PERMISSIONS.SUPER]
        },
        '/plataforma/contas': {
            requiredScope: SERVER_SCOPES.MATRIZ,
            requiredPermission: 'contas:view',
            fullAccessPermissions: [FULL_ACCESS_PERMISSIONS.MATRIZ, FULL_ACCESS_PERMISSIONS.SUPER]
        },
        '/plataforma/planos-contas': {
            requiredScope: SERVER_SCOPES.MATRIZ,
            requiredPermission: 'plans_contas:manage',
            fullAccessPermissions: [FULL_ACCESS_PERMISSIONS.MATRIZ, FULL_ACCESS_PERMISSIONS.SUPER]
        },
        '/plataforma/planos-franquias': {
            requiredScope: SERVER_SCOPES.MATRIZ,
            requiredPermission: 'plans_franquias:manage',
            fullAccessPermissions: [FULL_ACCESS_PERMISSIONS.MATRIZ, FULL_ACCESS_PERMISSIONS.SUPER]
        },
        '/galeria/gerenciamento': {
            requiredScope: SERVER_SCOPES.MATRIZ,
            requiredPermission: 'galeria_global:manage',
            fullAccessPermissions: [FULL_ACCESS_PERMISSIONS.MATRIZ, FULL_ACCESS_PERMISSIONS.SUPER]
        }
    };
    
    const routeConfig = routePermissions[routePath];
    
    if (!routeConfig) {
        log('debug', `Route ${routePath} não tem configuração de permissões, permitindo acesso`);
        return true;
    }
    
    // Verificar FULL ACCESS primeiro
    const hasFullAccess = routeConfig.fullAccessPermissions.some(perm => 
        permissions.includes(perm)
    );
    
    if (hasFullAccess) {
        log('debug', `Acesso a ${routePath} via FULL ACCESS`);
        return true;
    }
    
    // Verificar scope (APENAS LOG SE NEGADO)
    if (scope !== routeConfig.requiredScope) {
        // SILENCIOSO - só loga uma vez por rota
        const logKey = `scope_denied_${routePath}_${scope}`;
        if (!sessionStorage.getItem(logKey)) {
            log('debug', `Scope ${scope} não permite acesso a ${routePath} (requer ${routeConfig.requiredScope})`);
            sessionStorage.setItem(logKey, 'true');
        }
        return false;
    }
    
    // Verificar permissão específica
    const hasPermission = permissions.includes(routeConfig.requiredPermission);
    
    if (!hasPermission) {
        const logKey = `perm_denied_${routePath}`;
        if (!sessionStorage.getItem(logKey)) {
            log('debug', `Acesso a ${routePath} negado por falta de permissão específica`);
            sessionStorage.setItem(logKey, 'true');
        }
    }
    
    return hasPermission;
};

/**
 * ✅ Verifica se o usuário é Matriz Admin (OTIMIZADO)
 */
export const isMatrizAdmin = (user) => {
    if (!user || !user.role) return false;
    
    const scope = getUserScope(user);
    const permissions = user.permissions || user.role.permissions || [];
    
    // SILENCIOSO - só loga uma vez por sessão
    const logKey = 'matriz_admin_check';
    const shouldLog = !sessionStorage.getItem(logKey);
    
    if (scope === SERVER_SCOPES.MATRIZ) {
        if (shouldLog) {
            log('debug', `isMatrizAdmin: scope "${scope}" é matriz`);
            sessionStorage.setItem(logKey, 'true');
        }
        return true;
    }
    
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.MATRIZ)) {
        if (shouldLog) {
            log('debug', `isMatrizAdmin: tem permissão "matriz_admin:full_access"`);
            sessionStorage.setItem(logKey, 'true');
        }
        return true;
    }
    
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.SUPER)) {
        if (shouldLog) {
            log('debug', `isMatrizAdmin: tem permissão "super_admin:bypass_all_checks"`);
            sessionStorage.setItem(logKey, 'true');
        }
        return true;
    }
    
    return false;
};

/**
 * ✅ Obtém o scope do usuário (SILENCIOSO)
 */
export const getUserScope = (user) => {
    if (!user || !user.role) return SERVER_SCOPES.CONTA;
    
    // Prioridade 1: scope do role (vindo do servidor)
    if (user.role.scope && Object.values(SERVER_SCOPES).includes(user.role.scope)) {
        return user.role.scope;
    }
    
    // Prioridade 2: scope direto do user
    if (user.scope && Object.values(SERVER_SCOPES).includes(user.scope)) {
        return user.scope;
    }
    
    // Prioridade 3: converter role name para scope
    const roleNameToScope = {
        'matriz_admin': SERVER_SCOPES.MATRIZ,
        'franquia_admin': SERVER_SCOPES.FRANQUIA,
        'conta_admin': SERVER_SCOPES.CONTA,
        'terceiro': SERVER_SCOPES.TERCEIRO,
        'cliente': SERVER_SCOPES.CLIENTE,
        'admin': SERVER_SCOPES.CONTA,
        'profissional': SERVER_SCOPES.CONTA,
        'gerente': SERVER_SCOPES.CONTA,
        'user': SERVER_SCOPES.CONTA
    };
    
    const roleName = user.role.name || '';
    if (roleName && roleNameToScope[roleName]) {
        return roleNameToScope[roleName];
    }
    
    return SERVER_SCOPES.CONTA;
};

/**
 * ✅ Debug aprimorado (SILENCIOSO)
 */
export const debugPermissions = (user) => {
    if (!user) return { error: 'Usuário não autenticado' };
    
    const permissions = user.permissions || user.role?.permissions || [];
    const scope = getUserScope(user);
    
    const debugData = {
        id: user._id || user.id,
        email: user.email,
        roleName: user.role?.name,
        roleScope: user.role?.scope,
        userScope: scope,
        permissionsCount: permissions.length,
        hasSuperAdmin: permissions.includes(FULL_ACCESS_PERMISSIONS.SUPER),
        hasMatrizAdmin: isMatrizAdmin(user),
        hasContaAdmin: isContaAdmin(user),
        accountId: user.accountId || user.account?._id,
        validAccountId: getValidAccountId(user),
        // Amostra de permissões (apenas 5 para não poluir)
        samplePermissions: permissions.slice(0, 5)
    };
    
    // Log silencioso - só mostra uma vez
    const logKey = 'debug_permissions_logged';
    if (!sessionStorage.getItem(logKey)) {
        log('info', 'Estrutura do usuário:', debugData);
        sessionStorage.setItem(logKey, 'true');
    }
    
    return debugData;
};

// ✅ Restante das funções mantidas (mas com logs otimizados)
export const hasAnyPermission = (user, requiredPermissions) => {
    if (!user || !user.role) return false;
    
    if (hasPermission(user, 'super_admin:bypass_all_checks')) {
        return true;
    }
    
    return Array.isArray(requiredPermissions) && 
           requiredPermissions.some(permission => hasPermission(user, permission));
};

export const hasAllPermissions = (user, requiredPermissions) => {
    if (!user || !user.role) return false;
    
    if (hasPermission(user, 'super_admin:bypass_all_checks')) {
        return true;
    }
    
    return Array.isArray(requiredPermissions) && 
           requiredPermissions.every(permission => hasPermission(user, permission));
};

export const hasScope = (user, scopes) => {
    if (!user || !user.role) return false;
    
    const userScope = getUserScope(user);
    const allowedScopes = Array.isArray(scopes) ? scopes : [scopes];
    
    const result = allowedScopes.includes(userScope);
    
    // Log silencioso
    const logKey = `has_scope_${userScope}_${allowedScopes.join('_')}`;
    if (!sessionStorage.getItem(logKey)) {
        log('debug', `hasScope: ${userScope} in [${allowedScopes.join(', ')}] = ${result ? '✅' : '❌'}`);
        sessionStorage.setItem(logKey, 'true');
    }
    
    return result;
};

export const isFranquiaAdmin = (user) => {
    if (!user || !user.role) return false;
    
    const scope = getUserScope(user);
    const permissions = user.permissions || user.role.permissions || [];
    
    if (scope === SERVER_SCOPES.FRANQUIA) return true;
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.FRANQUIA)) return true;
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.MATRIZ)) return true;
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.SUPER)) return true;
    
    return false;
};

export const isContaAdmin = (user) => {
    if (!user || !user.role) return false;
    
    const scope = getUserScope(user);
    const permissions = user.permissions || user.role.permissions || [];
    
    if (scope === SERVER_SCOPES.CONTA) return true;
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.CONTA)) return true;
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.FRANQUIA)) return true;
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.MATRIZ)) return true;
    if (permissions.includes(FULL_ACCESS_PERMISSIONS.SUPER)) return true;
    
    return false;
};

export const isAdmin = (user) => {
    if (!user || !user.role) return false;
    
    return isMatrizAdmin(user) || isFranquiaAdmin(user) || isContaAdmin(user);
};

export const hasAccountAccess = (user) => {
    if (!user) return false;
    
    if (isMatrizAdmin(user)) {
        log('debug', 'Matriz Admin: acesso concedido (pode não ter conta vinculada)');
        return true;
    }
    
    return !!(user.account || 
              user.accountId || 
              (user.accounts && user.accounts.length > 0) ||
              getUserScope(user) === SERVER_SCOPES.CONTA);
};

export const canView = (user, resource) => {
    return hasAnyPermission(user, [
        `${resource}:view`,
        `${resource}:view_all`,
        `${resource}:edit`,
        `${resource}:edit_all`,
        `${resource}:create`,
        `${resource}:delete`
    ]);
};

export const canEdit = (user, resource) => {
    return hasAnyPermission(user, [
        `${resource}:edit`,
        `${resource}:edit_all`
    ]);
};

// ✅ Constantes para exportação
export const SUBSCRIPTION_PERMISSIONS = {
    CONTA: 'subscription_conta:manage',
    FRANQUIA: 'franchise_subscription:manage'
};

export const VALID_SCOPES = Object.values(SERVER_SCOPES);
export const FULL_ACCESS = FULL_ACCESS_PERMISSIONS;

export default {
    getValidAccountId,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasScope,
    isAdmin,
    isMatrizAdmin,
    isFranquiaAdmin,
    isContaAdmin,
    getUserScope,
    hasAccountAccess,
    canView,
    canEdit,
    canAccessRoute,
    debugPermissions,
    SUBSCRIPTION_PERMISSIONS,
    VALID_SCOPES,
    FULL_ACCESS,
    SERVER_SCOPES
};